import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import PhotoUploadControl from "../components/photos/PhotoUploadControl";
import PhotoAvatar from "../components/photos/PhotoAvatar";
import { 
  ArrowLeft, Users, Plus, Trash2, Tag, Edit2, Check, X, UserPlus, Shield
} from "lucide-react";
import { toast } from "sonner";
import { useTheme, useThemeClasses } from "../hooks/useTheme";
import { useUser } from "../context/UserContext";
import { useSwipeToClose } from "../hooks/useSwipeToClose";
import { API } from "../utils/constants";

export const TeamPage = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const theme = useThemeClasses(isDark);
  const { user, isCoach, loading: userLoading } = useUser();
  const [athletes, setAthletes] = useState([]);
  const [allAthletes, setAllAthletes] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [findingAthlete, setFindingAthlete] = useState(false);
  const findAthlete = async () => {
    if (!inviteEmail.trim()) return;
    setFindingAthlete(true);
    try {
      const { data } = await axios.get(`${API}/athletes`, { params: { email: inviteEmail.trim() } });
      setAllAthletes(data);
      if (!data.length) toast.error('No available player found with this email');
    } catch { toast.error('Could not find this player'); }
    finally { setFindingAthlete(false); }
  };
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  
  // Group state
  const [groups, setGroups] = useState([]);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupColor, setNewGroupColor] = useState('#8b5cf6');
  const [showGroupAssign, setShowGroupAssign] = useState(null);
  const [selectedGroupFilter, setSelectedGroupFilter] = useState(null); // Filter by group

  // Co-coach state
  const [showCoCoachModal, setShowCoCoachModal] = useState(false);
  const [showCoCoachAssign, setShowCoCoachAssign] = useState(null);
  const [coCoachEmail, setCoCoachEmail] = useState('');
  const [collaborations, setCollaborations] = useState({
    incoming_pending: [],
    outgoing_pending: [],
    accepted_as_head: [],
    accepted_as_co_coach: []
  });
  
  // MyGoal style preset colors (5 colors)
  const groupColors = [
    { value: '#8b5cf6', name: 'Purple' },
    { value: '#22c55e', name: 'Green' },
    { value: '#f97316', name: 'Orange' },
    { value: '#3b82f6', name: 'Blue' },
    { value: '#ec4899', name: 'Pink' },
  ];

  // Lock body scroll when modal is open
  useEffect(() => {
    if (showInviteModal) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${window.scrollY}px`;
    } else {
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
    };
  }, [showInviteModal]);

  // Swipe handlers
  const inviteSwipe = useSwipeToClose(() => setShowInviteModal(false));
  const groupAssignSwipe = useSwipeToClose(() => setShowGroupAssign(null));
  const coCoachSwipe = useSwipeToClose(() => setShowCoCoachModal(false));

  useEffect(() => {
    const fetchTeam = async () => {
      if (!user?.id) return;
      try {
        const userId = user.id;
        const [teamRes, allAthletesRes, groupsRes, collaborationsRes] = await Promise.all([
          axios.get(`${API}/coach/team?user_id=${userId}`),
          axios.get(`${API}/athletes`),
          axios.get(`${API}/groups?coach_id=${userId}`),
          axios.get(`${API}/coach/co-coach/collaborations?coach_id=${userId}`)
        ]);
        setAthletes(teamRes.data);
        setAllAthletes(allAthletesRes.data);
        setGroups(groupsRes.data);
        setCollaborations(collaborationsRes.data);
      } catch (e) {
        console.error("Error fetching team:", e);
      } finally {
        setLoading(false);
      }
    };
    if (!userLoading && user) {
      fetchTeam();
    }
  }, [user, userLoading]);

  // Group management functions
  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) {
      toast.error("Please enter a group name");
      return;
    }
    try {
      const res = await axios.post(`${API}/groups?coach_id=${user.id}`, {
        name: newGroupName,
        color: newGroupColor
      });
      setGroups([...groups, res.data]);
      setSelectedGroupFilter(res.data.id);
      setNewGroupName('');
      setNewGroupColor('#8b5cf6');
      setShowGroupModal(false);
      toast.success("Group created!");
    } catch (e) {
      toast.error("Failed to create group");
    }
  };

  const handleUpdateGroup = async () => {
    if (!editingGroup || !newGroupName.trim()) return;
    try {
      await axios.put(`${API}/groups/${editingGroup.id}?coach_id=${user.id}`, {
        name: newGroupName,
        color: newGroupColor
      });
      setGroups(groups.map(g => g.id === editingGroup.id ? {...g, name: newGroupName, color: newGroupColor} : g));
      setEditingGroup(null);
      setNewGroupName('');
      setNewGroupColor('#8b5cf6');
      setShowGroupModal(false);
      toast.success("Group updated!");
    } catch (e) {
      toast.error("Failed to update group");
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm("Delete this group?")) return;
    try {
      await axios.delete(`${API}/groups/${groupId}?coach_id=${user.id}`);
      setGroups(groups.filter(g => g.id !== groupId));
      toast.success("Group deleted!");
    } catch (e) {
      toast.error("Failed to delete group");
    }
  };

  const handleToggleAthleteGroup = async (athleteId, groupId) => {
    const group = groups.find(g => g.id === groupId);
    if (!group) return;
    
    const isInGroup = group.athlete_ids?.includes(athleteId);
    try {
      if (isInGroup) {
        await axios.delete(`${API}/groups/${groupId}/athletes/${athleteId}?coach_id=${user.id}`);
        setGroups(groups.map(g => g.id === groupId ? {...g, athlete_ids: g.athlete_ids.filter(id => id !== athleteId)} : g));
      } else {
        await axios.post(`${API}/groups/${groupId}/athletes/${athleteId}?coach_id=${user.id}`);
        setGroups(groups.map(g => g.id === groupId ? {...g, athlete_ids: [...(g.athlete_ids || []), athleteId]} : g));
      }
    } catch (e) {
      toast.error("Failed to update group");
    }
  };

  const ownedGroups = groups.filter(group => !group.is_read_only);
  const readOnlyGroups = groups.filter(group => group.is_read_only);
  const isReadOnlyCoCoachTeam = groups.length > 0 && ownedGroups.length === 0;

  const refreshTeamData = async () => {
    if (!user?.id) return;
    const [teamRes, groupsRes, collaborationsRes] = await Promise.all([
      axios.get(`${API}/coach/team?user_id=${user.id}`),
      axios.get(`${API}/groups?coach_id=${user.id}`),
      axios.get(`${API}/coach/co-coach/collaborations?coach_id=${user.id}`)
    ]);
    setAthletes(teamRes.data);
    setGroups(groupsRes.data);
    setCollaborations(collaborationsRes.data);
  };

  const handleInviteCoCoach = async () => {
    if (!coCoachEmail.trim()) {
      toast.error("Please enter a coach email");
      return;
    }
    try {
      await axios.post(`${API}/coach/co-coach/invite-by-email`, {
        head_coach_id: user.id,
        co_coach_email: coCoachEmail.trim().toLowerCase()
      });
      toast.success("Co-coach invitation sent");
      setCoCoachEmail('');
      await refreshTeamData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to invite co-coach");
    }
  };

  const handleAcceptCollaboration = async (collaborationId) => {
    try {
      await axios.put(`${API}/coach/co-coach/collaborations/${collaborationId}/accept?coach_id=${user.id}`);
      toast.success("Invitation accepted");
      await refreshTeamData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to accept invitation");
    }
  };

  const handleRemoveCollaboration = async (collaborationId) => {
    try {
      await axios.delete(`${API}/coach/co-coach/collaborations/${collaborationId}?coach_id=${user.id}`);
      toast.success("Co-coach link removed");
      await refreshTeamData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove collaboration");
    }
  };

  const handleToggleCoCoachGroup = async (groupId, coCoachId) => {
    const group = groups.find(g => g.id === groupId);
    if (!group || group.is_read_only) return;

    const isAssigned = (group.co_coach_ids || []).includes(coCoachId);
    try {
      if (isAssigned) {
        await axios.delete(`${API}/groups/${groupId}/co-coaches/${coCoachId}?coach_id=${user.id}`);
      } else {
        await axios.post(`${API}/groups/${groupId}/co-coaches/${coCoachId}?coach_id=${user.id}`);
      }
      await refreshTeamData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update co-coach group assignment");
    }
  };

  const getAthleteGroups = (athleteId) => {
    return groups.filter(g => g.athlete_ids?.includes(athleteId));
  };

  // Filter athletes based on selected group
  const filteredAthletes = selectedGroupFilter
    ? athletes.filter(athlete => {
        const group = groups.find(g => g.id === selectedGroupFilter);
        return group?.athlete_ids?.includes(athlete.id);
      })
    : athletes;

  // Get selected group info for display
  const selectedGroup = selectedGroupFilter 
    ? groups.find(g => g.id === selectedGroupFilter) 
    : null;

  const handleInvite = async (athleteId) => {
    try {
      await axios.post(`${API}/coach/invite`, { athlete_id: athleteId, coach_id: user?.id });
      toast.success("Invitation sent!");
      await refreshTeamData();
      setShowInviteModal(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send invitation");
    }
  };

  const handleRemove = async (connectionId, athleteName) => {
    if (!connectionId) {
      toast.error("This athlete is visible through read-only group access");
      return;
    }
    if (!window.confirm(`Remove ${athleteName} from your team?`)) return;
    try {
      await axios.delete(`${API}/coach/connection/${connectionId}`);
      toast.success("Athlete removed from team");
      setAthletes(athletes.filter(a => a.connection_id !== connectionId));
    } catch (e) {
      toast.error("Failed to remove athlete");
    }
  };

  const availableAthletes = allAthletes.filter(
    a => !athletes.find(t => t.id === a.id)
  );

  return (
    <div className={`min-h-screen ${theme.bg} flex flex-col transition-colors duration-300`} data-testid="page-team">
      <header className="flex items-center justify-between p-4 pt-6">
        <button data-testid="back-button" onClick={() => navigate("/")} className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${theme.button}`}>
          <ArrowLeft className={`w-5 h-5 ${theme.textPrimary}`} />
        </button>
        <h1 className={`text-xl font-bold ${theme.textPrimary}`}>My Team</h1>
        <div className="flex gap-2">
          {!isReadOnlyCoCoachTeam && (
            <button
              onClick={() => { setEditingGroup(null); setNewGroupName(''); setNewGroupColor('#8b5cf6'); setShowGroupModal(true); }}
              className={`w-10 h-10 rounded-full flex items-center justify-center ${theme.button}`}
              data-testid="manage-groups-button"
            >
              <Tag className={`w-5 h-5 ${theme.textPrimary}`} />
            </button>
          )}
          <button
            onClick={() => setShowCoCoachModal(true)}
            className={`w-10 h-10 rounded-full flex items-center justify-center ${theme.button}`}
            data-testid="manage-co-coaches-button"
          >
            <UserPlus className={`w-5 h-5 ${theme.textPrimary}`} />
          </button>
          {!isReadOnlyCoCoachTeam && (
            <button
              onClick={() => setShowInviteModal(true)}
              className="w-10 h-10 rounded-full flex items-center justify-center bg-gradient-to-r from-[#493986] to-[#92a7e8]"
              data-testid="invite-athlete-button"
            >
              <Plus className="w-5 h-5 text-white" />
            </button>
          )}
        </div>
      </header>

      {/* Groups Bar - Clickable for filtering */}
      {groups.length > 0 && (
        <div className="px-4 mb-2">
          <div className="flex gap-2 overflow-x-auto pb-2">
            {/* All Athletes Button */}
            <button
              onClick={() => setSelectedGroupFilter(null)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full flex-shrink-0 transition-all ${
                selectedGroupFilter === null
                  ? 'bg-gradient-to-r from-[#493986] to-[#92a7e8] text-white'
                  : isDark ? 'bg-white/10 text-white/70 hover:bg-white/20' : 'bg-[#493986]/10 text-[#493986]/70 hover:bg-[#493986]/20'
              }`}
              data-testid="filter-all-athletes"
            >
              <Users className="w-3.5 h-3.5" />
              <span className="text-xs font-medium">All</span>
              <span className={`text-xs ${selectedGroupFilter === null ? 'text-white/80' : ''}`}>({athletes.length})</span>
            </button>
            
            {/* Group Filter Buttons */}
            {groups.map(group => {
              const isSelected = selectedGroupFilter === group.id;
              const athleteCount = group.athlete_ids?.length || 0;
              return (
                <button 
                  key={group.id}
                  onClick={() => setSelectedGroupFilter(isSelected ? null : group.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full flex-shrink-0 transition-all ${
                    isSelected ? 'ring-2 ring-offset-2' : 'hover:scale-105'
                  }`}
                  style={{ 
                    backgroundColor: isSelected ? group.color : `${group.color}20`, 
                    border: `1px solid ${group.color}`,
                    color: isSelected ? 'white' : group.color,
                    ringColor: group.color,
                    ringOffsetColor: isDark ? '#0a0a0f' : '#fff'
                  }}
                  data-testid={`filter-group-${group.id}`}
                >
                  {group.photo_url ? <PhotoAvatar photoUrl={group.photo_url} name={group.name} className="h-6 w-6 rounded-full text-xs" /> : <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: isSelected ? 'white' : group.color }} />}
                  <span className="text-xs font-medium">{group.name}</span>
                  <span className={`text-xs ${isSelected ? 'text-white/80' : ''}`}>({athleteCount})</span>
                  {group.is_read_only && <Shield className="w-3 h-3" />}
                </button>
              );
            })}
            
            {/* Edit Groups Button (for non read-only) */}
            {!isReadOnlyCoCoachTeam && (
              <button 
                onClick={() => { setEditingGroup(null); setNewGroupName(''); setNewGroupColor('#8b5cf6'); setShowGroupModal(true); }}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-full flex-shrink-0 border border-dashed transition-colors ${
                  isDark ? 'border-white/30 text-white/50 hover:border-white/50 hover:text-white/70' : 'border-[#493986]/30 text-[#493986]/50 hover:border-[#493986]/50 hover:text-[#493986]/70'
                }`}
                data-testid="add-group-inline"
              >
                <Plus className="w-3.5 h-3.5" />
                <span className="text-xs font-medium">Add Group</span>
              </button>
            )}
          </div>
        </div>
      )}


      {readOnlyGroups.length > 0 && (
        <div className="px-4 mb-2" data-testid="co-coach-read-only-banner">
          <div className={`p-2.5 rounded-xl border text-xs ${isDark ? 'bg-white/5 border-white/10 text-white/80' : 'bg-[#493986]/5 border-[#493986]/15 text-[#493986]/80'}`}>
            You can view assigned co-coach groups but cannot edit them.
          </div>
        </div>
      )}

      <div className="p-4 flex-1">
        {!loading && selectedGroup && <div className={`mb-4 rounded-2xl border p-4 ${theme.card}`}>
          <div className="mb-3 flex items-center gap-3"><PhotoAvatar photoUrl={selectedGroup.photo_url} name={selectedGroup.name} className="h-20 w-20 rounded-2xl text-2xl" fallback={<Users className="h-8 w-8" />} /><div className="min-w-0 flex-1"><h2 className={`font-semibold ${theme.textPrimary}`}>{selectedGroup.name}</h2><p className={`text-sm ${theme.textMuted}`}>{filteredAthletes.length} athletes</p></div>{!selectedGroup.is_read_only && <button type="button" aria-label={`Edit ${selectedGroup.name}`} data-testid="edit-selected-group" onClick={() => { setEditingGroup(selectedGroup); setNewGroupName(selectedGroup.name); setNewGroupColor(selectedGroup.color); setShowGroupModal(true); }} className="rounded-xl p-2 hover:bg-violet-500/10"><Edit2 size={18} /></button>}</div>
          {!selectedGroup.is_read_only && <PhotoUploadControl key={selectedGroup.id} endpoint={`/groups/${selectedGroup.id}/photo`} photoUrl={selectedGroup.photo_url} label="group photo" onSaved={photo_url => { setGroups(current => current.map(group => group.id === selectedGroup.id ? { ...group, photo_url } : group)); toast.success(photo_url ? 'Group photo saved' : 'Group photo removed'); }} />}
        </div>}

        {loading ? (
          <div className={`text-center py-12 ${theme.textMuted}`}>Loading...</div>
        ) : athletes.length === 0 ? (
          <div className={`text-center py-12`}>
            <Users className={`w-16 h-16 mx-auto mb-4 ${theme.textMuted}`} />
            <h3 className={`font-semibold mb-2 ${theme.textPrimary}`}>No athletes yet</h3>
            <p className={`text-sm mb-4 ${theme.textMuted}`}>Invite athletes to join your team</p>
            <button
              onClick={() => setShowInviteModal(true)}
              className="px-6 py-3 bg-gradient-to-r from-[#493986] to-[#92a7e8] text-white font-bold rounded-xl"
            >
              Invite Athletes
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Empty state for filtered group */}
            {filteredAthletes.length === 0 && selectedGroup && (
              <div className={`text-center py-8`}>
                <Users className={`w-12 h-12 mx-auto mb-3`} style={{ color: selectedGroup.color }} />
                <h3 className={`font-semibold mb-2 ${theme.textPrimary}`}>No athletes in {selectedGroup.name}</h3>
                <p className={`text-sm mb-4 ${theme.textMuted}`}>Add athletes to this group from the team list</p>
                <button
                  onClick={() => setSelectedGroupFilter(null)}
                  className="px-4 py-2 rounded-xl font-medium"
                  style={{ backgroundColor: `${selectedGroup.color}20`, color: selectedGroup.color }}
                >
                  View All Athletes
                </button>
              </div>
            )}

            {/* Athletes List */}
            {filteredAthletes.map((athlete) => {
              const athleteGroups = getAthleteGroups(athlete.id);
              return (
              <div
                key={athlete.id}
                className={`p-4 rounded-2xl ${theme.card} border backdrop-blur-xl`}
                data-testid={`athlete-${athlete.id}`}
              >
                {/* Row 1: Avatar, Name, Email, Delete button */}
                <div className="flex items-center gap-3">
                  <PhotoAvatar photoUrl={athlete.photo_url} name={athlete.name} className="h-11 w-11 rounded-xl text-lg" />
                  <div className="flex-1 min-w-0">
                    <h3 className={`font-semibold ${theme.textPrimary}`}>{athlete.name}</h3>
                    <p className={`text-xs ${theme.textMuted}`}>
                      {athlete.email || 'No email'}
                    </p>
                  </div>
                  {athlete.connection_id && (
                    <button
                      onClick={() => handleRemove(athlete.connection_id, athlete.name)}
                      className={`w-9 h-9 rounded-xl ${theme.button} flex items-center justify-center flex-shrink-0`}
                    >
                      <Trash2 className={`w-4 h-4 ${theme.textMuted}`} />
                    </button>
                  )}
                </div>

                {/* Row 2: Status + Group labels */}
                <div className="flex items-center gap-1.5 flex-wrap mt-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    athlete.connection_status === 'accepted' || athlete.connection_status === 'assigned_group'
                      ? isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-600'
                      : isDark ? 'bg-yellow-500/20 text-yellow-400' : 'bg-yellow-100 text-yellow-600'
                  }`}>
                    {athlete.connection_status === 'accepted'
                      ? 'Active'
                      : athlete.connection_status === 'assigned_group'
                        ? 'Read-only group'
                        : 'Pending'}
                  </span>
                  {athleteGroups.map(group => (
                    <span 
                      key={group.id}
                      className="text-xs px-2 py-0.5 rounded-full flex items-center gap-1"
                      style={{ 
                        backgroundColor: `${group.color}20`, 
                        color: group.color,
                        border: `1px solid ${group.color}40`
                      }}
                    >
                      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: group.color }} />
                      {group.name}
                    </span>
                  ))}
                  {athlete.connection_id && (
                    <button
                      onClick={() => setShowGroupAssign(athlete.id)}
                      className={`text-xs px-2 py-0.5 rounded-full border border-dashed ${isDark ? 'border-white/30 text-white/50 hover:border-white/50 hover:text-white/70' : 'border-[#493986]/30 text-[#493986]/50 hover:border-[#493986]/50 hover:text-[#493986]/70'} transition-colors`}
                      data-testid={`assign-group-${athlete.id}`}
                    >
                      <Plus className="w-3 h-3 inline -mt-0.5" />
                    </button>
                  )}
                </div>

                {/* Row 3: Stats */}
                <div className={`flex items-center gap-2 mt-3 pt-3 border-t ${isDark ? 'border-white/10' : 'border-[#493986]/10'}`}>
                  <div className={`flex-1 h-10 rounded-xl ${isDark ? 'bg-white/10' : 'bg-[#493986]/10'} flex items-center justify-center gap-1.5`}>
                    <span className={`text-sm font-bold ${theme.textPrimary}`}>{athlete.total_sessions || 0}</span>
                    <span className={`text-xs ${theme.textMuted}`}>sessions</span>
                  </div>
                  <div className={`flex-1 h-10 rounded-xl ${isDark ? 'bg-white/10' : 'bg-[#493986]/10'} flex items-center justify-center gap-1.5`}>
                    <span className={`text-sm font-bold ${theme.textPrimary}`}>{athlete.total_goals || 0}</span>
                    <span className={`text-xs ${theme.textMuted}`}>goals</span>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 z-50 flex items-end justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowInviteModal(false)} />
          <div 
            className={`relative w-full max-h-[80vh] overflow-y-auto rounded-t-3xl ${isDark ? 'bg-[#0a0a0f]' : 'bg-white'} p-4 pb-8`}
            style={{ 
              transform: `translateY(${inviteSwipe.translateY}px)`,
              transition: inviteSwipe.isDragging ? 'none' : 'transform 0.2s ease-out'
            }}
            onTouchStart={inviteSwipe.onTouchStart}
            onTouchMove={inviteSwipe.onTouchMove}
            onTouchEnd={inviteSwipe.onTouchEnd}
          >
            <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-4 cursor-grab" />
            <h2 className={`text-xl font-bold mb-4 ${theme.textPrimary}`}>Invite Athlete</h2>
            
            <div className="flex gap-2 mb-4">
              <input type="email" aria-label="Player email" value={inviteEmail} onChange={e => { setInviteEmail(e.target.value); setAllAthletes([]); }} placeholder="Player email address" className={`flex-1 min-w-0 p-3 border rounded-xl ${theme.input}`} />
              <button disabled={findingAthlete || !inviteEmail.trim()} onClick={findAthlete} className="px-4 py-3 rounded-xl bg-gradient-to-r from-[#493986] to-[#92a7e8] text-white disabled:opacity-50">{findingAthlete ? 'Searching…' : 'Find player'}</button>
            </div>
            {availableAthletes.length === 0 ? (
              <p className={`text-center py-8 ${theme.textMuted}`}>Enter the player’s email address to invite them</p>
            ) : (
              <div className="space-y-2">
                {availableAthletes.map(athlete => (
                  <div 
                    key={athlete.id}
                    className={`p-3 rounded-xl flex items-center justify-between ${theme.card} border`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#493986] to-[#92a7e8] flex items-center justify-center">
                        <span className="text-white font-bold">
                          {athlete.name?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className={`font-medium ${theme.textPrimary}`}>{athlete.name}</p>
                        <p className={`text-xs ${theme.textMuted}`}>{athlete.email || inviteEmail}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleInvite(athlete.id)}
                      className="px-4 py-2 bg-gradient-to-r from-[#493986] to-[#92a7e8] text-white text-sm font-medium rounded-xl"
                    >
                      Invite
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Group Management Modal */}
      {showGroupModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => { setShowGroupModal(false); setEditingGroup(null); }} />
          <div className={`relative w-full max-w-md rounded-2xl ${isDark ? 'bg-[#0a0a0f]' : 'bg-white'} p-6`}>
            <div className="flex items-center justify-between mb-6">
              <h2 className={`text-xl font-bold ${theme.textPrimary}`}>
                {editingGroup ? 'Edit Group' : 'Create Group'}
              </h2>
              <button 
                onClick={() => { setShowGroupModal(false); setEditingGroup(null); }}
                className={`w-8 h-8 rounded-full ${theme.button} flex items-center justify-center`}
              >
                <X className={`w-4 h-4 ${theme.textMuted}`} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${theme.textSecondary}`}>Group Name</label>
                <input
                  type="text"
                  placeholder="e.g., Senior Team, Beginners"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  className={`w-full px-4 py-3 rounded-xl border ${theme.input}`}
                  data-testid="group-name-input"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${theme.textSecondary}`}>Group Color</label>
                <div className="flex gap-3">
                  {groupColors.map((color) => (
                    <button
                      key={color.value}
                      onClick={() => setNewGroupColor(color.value)}
                      className={`w-10 h-10 rounded-xl flex items-center justify-center transition-transform ${newGroupColor === color.value ? 'scale-110 ring-2 ring-offset-2' : ''}`}
                      style={{ 
                        backgroundColor: color.value,
                        ringColor: color.value,
                        ringOffsetColor: isDark ? '#0a0a0f' : '#fff'
                      }}
                      title={color.name}
                      data-testid={`color-${color.name.toLowerCase()}`}
                    >
                      {newGroupColor === color.value && (
                        <Check className="w-5 h-5 text-white" />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Preview */}
              <div className={`p-3 rounded-xl ${isDark ? 'bg-white/5' : 'bg-[#493986]/5'}`}>
                <span className={`text-xs ${theme.textMuted}`}>Preview:</span>
                <div className="mt-2">
                  <span 
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium"
                    style={{ 
                      backgroundColor: `${newGroupColor}20`, 
                      color: newGroupColor,
                      border: `1px solid ${newGroupColor}`
                    }}
                  >
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: newGroupColor }} />
                    {newGroupName || 'Group Name'}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              {editingGroup && (
                <button
                  onClick={() => { handleDeleteGroup(editingGroup.id); setShowGroupModal(false); setEditingGroup(null); }}
                  className="px-4 py-3 rounded-xl border border-red-500/30 text-red-500 font-medium hover:bg-red-500/10 transition-colors"
                  data-testid="delete-group-button"
                >
                  Delete
                </button>
              )}
              <button
                onClick={() => { setShowGroupModal(false); setEditingGroup(null); }}
                className={`flex-1 px-4 py-3 rounded-xl ${theme.button} font-medium ${theme.textSecondary}`}
              >
                Cancel
              </button>
              <button
                onClick={editingGroup ? handleUpdateGroup : handleCreateGroup}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-[#493986] to-[#92a7e8] text-white font-bold rounded-xl"
                data-testid="save-group-button"
              >
                {editingGroup ? 'Save' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Athlete Group Assignment Modal */}
      {showGroupAssign && (
        <div className="fixed inset-0 z-50 flex items-end justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowGroupAssign(null)} />
          <div 
            className={`relative w-full max-h-[60vh] overflow-y-auto rounded-t-3xl ${isDark ? 'bg-[#0a0a0f]' : 'bg-white'} p-4 pb-8`}
            style={{ 
              transform: `translateY(${groupAssignSwipe.translateY}px)`,
              transition: groupAssignSwipe.isDragging ? 'none' : 'transform 0.2s ease-out'
            }}
            onTouchStart={groupAssignSwipe.onTouchStart}
            onTouchMove={groupAssignSwipe.onTouchMove}
            onTouchEnd={groupAssignSwipe.onTouchEnd}
          >
            <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-4 cursor-grab" />
            
            <h2 className={`text-lg font-bold mb-2 ${theme.textPrimary}`}>
              Assign to Groups
            </h2>
            <p className={`text-sm mb-4 ${theme.textMuted}`}>
              {athletes.find(a => a.id === showGroupAssign)?.name}
            </p>
            
            {ownedGroups.length === 0 ? (
              <div className={`text-center py-8`}>
                <Tag className={`w-12 h-12 mx-auto mb-3 ${theme.textMuted}`} />
                <p className={`${theme.textMuted} mb-3`}>No groups created yet</p>
                <button
                  onClick={() => { setShowGroupAssign(null); setShowGroupModal(true); }}
                  className="px-4 py-2 bg-gradient-to-r from-[#493986] to-[#92a7e8] text-white font-medium rounded-xl text-sm"
                >
                  Create First Group
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                {ownedGroups.map(group => {
                  const isInGroup = group.athlete_ids?.includes(showGroupAssign);
                  return (
                    <button
                      key={group.id}
                      onClick={() => handleToggleAthleteGroup(showGroupAssign, group.id)}
                      className={`w-full p-3 rounded-xl flex items-center justify-between transition-all ${
                        isInGroup 
                          ? '' 
                          : isDark ? 'hover:bg-white/5' : 'hover:bg-[#493986]/5'
                      }`}
                      style={isInGroup ? { 
                        backgroundColor: `${group.color}15`,
                        border: `1px solid ${group.color}40`
                      } : {
                        border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(73,57,134,0.1)'}`
                      }}
                      data-testid={`toggle-group-${group.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-8 h-8 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: `${group.color}30` }}
                        >
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: group.color }} />
                        </div>
                        <div className="text-left">
                          <span className={`font-medium ${theme.textPrimary}`}>{group.name}</span>
                          <p className={`text-xs ${theme.textMuted}`}>
                            {group.athlete_ids?.length || 0} athlete{group.athlete_ids?.length !== 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>
                      <div 
                        className={`w-6 h-6 rounded-full flex items-center justify-center ${
                          isInGroup ? '' : isDark ? 'border-2 border-white/20' : 'border-2 border-[#493986]/20'
                        }`}
                        style={isInGroup ? { backgroundColor: group.color } : {}}
                      >
                        {isInGroup && <Check className="w-4 h-4 text-white" />}
                      </div>
                    </button>
                  );
                })}
                
                <button
                  onClick={() => { setShowGroupAssign(null); setShowGroupModal(true); }}
                  className={`w-full p-3 rounded-xl flex items-center gap-3 border border-dashed ${isDark ? 'border-white/20 hover:border-white/40' : 'border-[#493986]/20 hover:border-[#493986]/40'} transition-colors`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isDark ? 'bg-white/10' : 'bg-[#493986]/10'}`}>
                    <Plus className={`w-4 h-4 ${theme.textMuted}`} />
                  </div>
                  <span className={`font-medium ${theme.textSecondary}`}>Create New Group</span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Co-Coach Management Modal */}
      {showCoCoachModal && (
        <div className="fixed inset-0 z-50 flex items-end justify-center" data-testid="co-coach-management-modal">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowCoCoachModal(false)} />
          <div
            className={`relative w-full max-h-[84vh] overflow-y-auto rounded-t-3xl ${isDark ? 'bg-[#0a0a0f]' : 'bg-white'} p-4 pb-8`}
            style={{
              transform: `translateY(${coCoachSwipe.translateY}px)`,
              transition: coCoachSwipe.isDragging ? 'none' : 'transform 0.2s ease-out'
            }}
            onTouchStart={coCoachSwipe.onTouchStart}
            onTouchMove={coCoachSwipe.onTouchMove}
            onTouchEnd={coCoachSwipe.onTouchEnd}
          >
            <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-4 cursor-grab" />
            <h2 className={`text-xl font-bold mb-4 ${theme.textPrimary}`}>Co-Coaches</h2>

            <div className={`p-3 rounded-xl border mb-4 ${theme.card}`}>
              <p className={`text-sm font-medium mb-2 ${theme.textSecondary}`}>Invite coach by email</p>
              <div className="flex gap-2">
                <input
                  data-testid="co-coach-email-input"
                  type="email"
                  value={coCoachEmail}
                  onChange={(e) => setCoCoachEmail(e.target.value)}
                  placeholder="coach@email.com"
                  className={`flex-1 px-3 py-2 rounded-xl border ${theme.input}`}
                />
                <button
                  data-testid="co-coach-send-invite"
                  onClick={handleInviteCoCoach}
                  className="px-4 py-2 rounded-xl bg-gradient-to-r from-[#493986] to-[#92a7e8] text-white font-medium"
                >
                  Invite
                </button>
              </div>
            </div>

            {collaborations.incoming_pending?.length > 0 && (
              <div className="mb-4" data-testid="co-coach-incoming-list">
                <p className={`text-sm font-semibold mb-2 ${theme.textSecondary}`}>Incoming invitations</p>
                <div className="space-y-2">
                  {collaborations.incoming_pending.map((invite) => (
                    <div key={invite.id} className={`p-3 rounded-xl border ${theme.card}`}>
                      <p className={`font-medium ${theme.textPrimary}`}>{invite.head_coach_name}</p>
                      <p className={`text-xs ${theme.textMuted}`}>{invite.head_coach_email}</p>
                      <div className="flex gap-2 mt-2">
                        <button
                          data-testid={`accept-co-coach-${invite.id}`}
                          onClick={() => handleAcceptCollaboration(invite.id)}
                          className="flex-1 py-2 rounded-xl bg-green-500 text-white text-sm font-medium"
                        >
                          Accept
                        </button>
                        <button
                          data-testid={`decline-co-coach-${invite.id}`}
                          onClick={() => handleRemoveCollaboration(invite.id)}
                          className={`flex-1 py-2 rounded-xl ${theme.button} ${theme.textSecondary} text-sm font-medium`}
                        >
                          Decline
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {collaborations.outgoing_pending?.length > 0 && (
              <div className="mb-4" data-testid="co-coach-outgoing-list">
                <p className={`text-sm font-semibold mb-2 ${theme.textSecondary}`}>Pending invitations</p>
                <div className="space-y-2">
                  {collaborations.outgoing_pending.map((invite) => (
                    <div key={invite.id} className={`p-3 rounded-xl border ${theme.card} flex items-center justify-between`}>
                      <div>
                        <p className={`font-medium ${theme.textPrimary}`}>{invite.co_coach_name}</p>
                        <p className={`text-xs ${theme.textMuted}`}>{invite.co_coach_email}</p>
                      </div>
                      <button
                        data-testid={`cancel-co-coach-${invite.id}`}
                        onClick={() => handleRemoveCollaboration(invite.id)}
                        className={`px-3 py-1.5 rounded-lg ${theme.button} ${theme.textSecondary} text-xs`}
                      >
                        Cancel
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mb-4" data-testid="co-coach-linked-list">
              <p className={`text-sm font-semibold mb-2 ${theme.textSecondary}`}>Linked co-coaches</p>
              {collaborations.accepted_as_head?.length === 0 ? (
                <p className={`text-sm ${theme.textMuted}`}>No linked co-coaches yet.</p>
              ) : (
                <div className="space-y-2">
                  {collaborations.accepted_as_head.map((link) => (
                    <div key={link.id} className={`p-3 rounded-xl border ${theme.card}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div>
                          <p className={`font-medium ${theme.textPrimary}`}>{link.co_coach_name}</p>
                          <p className={`text-xs ${theme.textMuted}`}>{link.co_coach_email}</p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            data-testid={`assign-groups-co-coach-${link.co_coach_id}`}
                            onClick={() => setShowCoCoachAssign(link)}
                            className={`px-2.5 py-1.5 rounded-lg ${theme.button} ${theme.textSecondary} text-xs`}
                          >
                            Assign groups
                          </button>
                          <button
                            data-testid={`remove-link-co-coach-${link.id}`}
                            onClick={() => handleRemoveCollaboration(link.id)}
                            className="px-2.5 py-1.5 rounded-lg bg-red-500 text-white text-xs"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {collaborations.accepted_as_head?.length > 0 && ownedGroups.length > 0 && (
              <div className="mb-4" data-testid="co-coach-access-matrix">
                <p className={`text-sm font-semibold mb-2 ${theme.textSecondary}`}>Co-coach access matrix</p>
                <div className="overflow-x-auto rounded-xl border" style={{ borderColor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(73,57,134,0.16)' }}>
                  <table className="w-full min-w-[560px] text-xs">
                    <thead>
                      <tr className={isDark ? 'bg-white/5' : 'bg-[#493986]/5'}>
                        <th className={`text-left px-3 py-2 font-semibold ${theme.textSecondary}`}>Co-coach</th>
                        {ownedGroups.map((group) => (
                          <th key={`matrix-header-${group.id}`} className={`px-3 py-2 text-left font-semibold ${theme.textSecondary}`}>
                            {group.name}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {collaborations.accepted_as_head.map((link) => (
                        <tr key={`matrix-row-${link.id}`} className={`border-t ${isDark ? 'border-white/10' : 'border-[#493986]/12'}`}>
                          <td className={`px-3 py-2 ${theme.textPrimary}`}>
                            <div className="font-medium">{link.co_coach_name}</div>
                            <div className={`text-[11px] ${theme.textMuted}`}>{link.co_coach_email}</div>
                          </td>
                          {ownedGroups.map((group) => {
                            const assigned = (group.co_coach_ids || []).includes(link.co_coach_id);
                            return (
                              <td key={`matrix-cell-${link.co_coach_id}-${group.id}`} className="px-3 py-2">
                                <button
                                  type="button"
                                  data-testid={`co-coach-matrix-toggle-${link.co_coach_id}-${group.id}`}
                                  onClick={() => handleToggleCoCoachGroup(group.id, link.co_coach_id)}
                                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${assigned ? 'bg-green-500' : (isDark ? 'bg-white/20' : 'bg-[#493986]/20')}`}
                                >
                                  <span
                                    className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${assigned ? 'translate-x-5' : 'translate-x-1'}`}
                                  />
                                </button>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}


            {collaborations.accepted_as_co_coach?.length > 0 && (
              <div className="mb-2" data-testid="co-coach-readonly-links">
                <p className={`text-sm font-semibold mb-2 ${theme.textSecondary}`}>You are co-coach for</p>
                <div className="space-y-2">
                  {collaborations.accepted_as_co_coach.map((link) => (
                    <div key={link.id} className={`p-3 rounded-xl border ${theme.card}`}>
                      <p className={`font-medium ${theme.textPrimary}`}>{link.head_coach_name}</p>
                      <p className={`text-xs ${theme.textMuted}`}>{link.assigned_group_count || 0} assigned groups</p>
                      <button
                        data-testid={`leave-co-coach-${link.id}`}
                        onClick={() => handleRemoveCollaboration(link.id)}
                        className={`mt-2 px-3 py-1.5 rounded-lg ${theme.button} ${theme.textSecondary} text-xs`}
                      >
                        Leave collaboration
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Co-Coach Group Assignment Modal */}
      {showCoCoachAssign && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="co-coach-group-assign-modal">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowCoCoachAssign(null)} />
          <div className={`relative w-full max-w-md rounded-2xl ${isDark ? 'bg-[#0a0a0f]' : 'bg-white'} p-4`}>
            <div className="flex items-center justify-between mb-3">
              <h3 className={`text-lg font-semibold ${theme.textPrimary}`}>Assign Groups</h3>
              <button
                onClick={() => setShowCoCoachAssign(null)}
                className={`w-8 h-8 rounded-full ${theme.button} flex items-center justify-center`}
              >
                <X className={`w-4 h-4 ${theme.textMuted}`} />
              </button>
            </div>
            <p className={`text-sm mb-3 ${theme.textSecondary}`}>{showCoCoachAssign.co_coach_name}</p>

            {ownedGroups.length === 0 ? (
              <p className={`text-sm ${theme.textMuted}`}>Create your own groups first to assign a co-coach.</p>
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {ownedGroups.map((group) => {
                  const isAssigned = (group.co_coach_ids || []).includes(showCoCoachAssign.co_coach_id);
                  return (
                    <button
                      key={group.id}
                      data-testid={`toggle-co-coach-group-${showCoCoachAssign.co_coach_id}-${group.id}`}
                      onClick={() => handleToggleCoCoachGroup(group.id, showCoCoachAssign.co_coach_id)}
                      className={`w-full p-3 rounded-xl border flex items-center justify-between ${isAssigned ? '' : theme.card}`}
                      style={isAssigned ? { borderColor: group.color, backgroundColor: `${group.color}18` } : undefined}
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: group.color }} />
                        <span className={`text-sm font-medium ${theme.textPrimary}`}>{group.name}</span>
                      </div>
                      <span className={`text-xs ${theme.textMuted}`}>{isAssigned ? 'Assigned' : 'Not assigned'}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default TeamPage;
